/**
 * Utilidad compartida para formateo y envío de mensajes por WhatsApp.
 * 
 * Resuelve problemas de codificación de emojis:
 * 1. Usa 'https://api.whatsapp.com/send/?phone=...&text=...' en lugar de 'wa.me'
 *    para evitar que la redirección HTTP 302 de Meta rompa los caracteres UTF-8 de 4 bytes (?).
 * 2. Limpia el selector de variación Unicode 16 (\uFE0F) que en navegadores/Windows
 *    suele renderizarse como '?' o caja con interrogación.
 * 3. Traduce comandos / shortcodes habituales de texto (ej. :lentes:, :reloj:) a emojis reales.
 */

const SHORTCODES_EMOJIS = {
  ':lentes:': '👓',
  ':anteojo:': '👓',
  ':anteojos:': '👓',
  ':gafas:': '👓',
  ':sobre:': '📋',
  ':reloj:': '🕐',
  ':calendario:': '🗓',
  ':sabado:': '🗓',
  ':sabados:': '🗓',
  ':gracias:': '😊',
  ':estrella:': '⭐',
  ':dinero:': '💰',
  ':tarjeta:': '💳',
  ':observaciones:': '📌',
  ':pin:': '📌',
  ':tiempo:': '⏱',
  ':ojo:': '👁',
  ':ojos:': '👁',
  ':info:': 'ℹ',
  ':check:': '✅',
  ':paquete:': '📦'
};

/**
 * Normaliza y limpia el texto del mensaje para máxima compatibilidad con WhatsApp.
 * Remueve \uFE0F (Variation Selector 16) que genera signos '?' en WhatsApp Web/Desktop.
 */
export function sanitizarMensajeWhatsApp(mensaje) {
  if (!mensaje) return '';
  let texto = String(mensaje);

  // Reemplazar atajos tipo comando (:lentes:) a emojis reales si los hubiera
  Object.entries(SHORTCODES_EMOJIS).forEach(([shortcode, emoji]) => {
    const escaped = shortcode.replace(/[-\/\\^$*+?.()|[\]{}]/g, '\\$&');
    texto = texto.replace(new RegExp(escaped, 'gi'), emoji);
  });

  // Eliminar selector de variación 16 invisible (\uFE0F)
  texto = texto.replace(/\uFE0F/g, '');

  return texto;
}

/**
 * Normaliza y formatea el número de teléfono para la API de WhatsApp.
 * Asegura formato internacional limpio sin '+', espacios ni guiones.
 */
export function formatearTelefonoWhatsApp(telefono) {
  if (!telefono) return '';
  let num = String(telefono).replace(/[\s\-\(\)\.]/g, '');
  if (num.startsWith('+')) num = num.slice(1);
  if (num.startsWith('54')) return num;
  if (num.startsWith('0')) return '54' + num.slice(1);
  return '54' + num;
}

/**
 * Construye la URL canónica y segura para abrir WhatsApp con el mensaje pre-cargado.
 * Usa api.whatsapp.com en lugar del acortador wa.me para prevenir corrupción de caracteres.
 */
export function construirUrlWhatsApp(telefono, mensaje) {
  const telLimpio = formatearTelefonoWhatsApp(telefono);
  const msgLimpio = sanitizarMensajeWhatsApp(mensaje);
  const encodedText = encodeURIComponent(msgLimpio);

  return `https://api.whatsapp.com/send/?phone=${telLimpio}&text=${encodedText}`;
}

/**
 * Abre directamente la conversación de WhatsApp en una nueva pestaña segura.
 */
export function abrirWhatsApp(telefono, mensaje) {
  const url = construirUrlWhatsApp(telefono, mensaje);
  if (typeof window !== 'undefined' && window.open) {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
  return url;
}
